import { useTranslation } from "react-i18next";
import { Card } from "reactstrap";
import Drawer from "../../../../components/Drawer";
import Form from "./Form";
import BulkActions from "./BulkActions";
import Topbar from "../../components/Topbar";
import Actions from "./Actions";
import { useColumns } from "./columns";
import NTable from "../../../../components/NTable";
import useSettingsCRUDHook from "../../hook";
import { useGroups } from "../OrganisationLevelGroups/context";
import { useDrawerProps } from "../../helpers/useDrawerProps";
import { useDeleteModal } from "../../helpers/useDeleteModal";
import { useManageForm } from "../../helpers/useManageForm";
import { useFetchResource } from "../../helpers/useFetchResource";
import { useAccess, useActionWithAccess } from "../../../../utils/hooks/access";
import {useNewTable} from "../../../../components/NTable/hook";
import {useQueryFilters} from "../../helpers/useQueryFilters";
import {useVisibleColumnKeys} from "../../helpers/useVisibleColumnKeys";
import {useOnExport} from "../../helpers/useOnExport";
import {useImportModal} from "../../helpers/useImportModal";
import {useInitialState} from "../../../../utils/hooks/useInitialState";
import { useAssignGroupings } from "../Jobs/BulkActions/useAssignGroupings";

function Locations() {
	const { t } = useTranslation();

	const {
		state: { mode, data, totalItems, loading, submitting, error },
		selected,
		fetch,
		createSubmit,
		updateSubmit,
		goToHelp,
		goToView,
		goToCreate,
		goToEdit,
		goToClone,
		remove,
		batchRemove,
		settingsExport,
		loadingExport,
		setData,
	} = useSettingsCRUDHook("/locations");

	const { hasAccess: canAdd } = useAccess(
		"settings.general.locations.canCreate",
	);

	const { groups } = useGroups();

	const { modal: deleteModal, open: openDeleteModal } = useDeleteModal({
		message: (data) =>
			Array.isArray(data)
				? t(
					"do-you-want-to-delete-locations", {
					count: data?.length
				}
				)
				: t("do-you-want-to-delete-location"),
		onConfirm: (data) =>
			Array.isArray(data) ? batchRemove(data) : remove(data),
        permission: "settings.general.locations.canDelete",
	});
	const { unarchiveModal, isFormOpen, formProps } = useManageForm({
		mode,
		selected,
		createSubmit,
		updateSubmit,
	});

	const columns = useColumns({
		edit: useActionWithAccess(
			goToEdit,
			"settings.general.locations.canEdit",
		),
		remove: openDeleteModal,
		clone: useActionWithAccess(
			goToClone,
			"settings.general.locations.canCreate",
		),
	});

    const initialState = useInitialState(() => {
		const hiddenColumns = ["address1", "state", "postalCode"];
		groups?.forEach((group) => {
            hiddenColumns.push(`${group.level}`.replace("_", ""));
		}, []);
        return { 
            columnVisibility: hiddenColumns,
        };
    });

	const table = useNewTable({
        data,
		columns,
        loading,
		totalItems,
        sessionFiltersKey: "locations",
        initialState,
	});

    const queryFilters = useQueryFilters(table);

	const { open: drawerOpen, title: drawerTitle } = useDrawerProps({
		mode,
		help: t("help"),
		view: t("view-location"),
		create: t("add-location"),
		edit: t("edit-location"),
		clone: t("clone-location"),
	});

	useFetchResource({ fetch, state: table.getState() });

    const visibleColumnKeys = useVisibleColumnKeys(table);

    const onExport = useOnExport({
        url: "/export/locations",
        exportCall: settingsExport,
        queryFilters,
        visibleColumnKeys
    });

    const { open: openImportModal, modal: importModal } = useImportModal({
        importUrl: "/locations/import",
        exportUrl: "/export/locations",
        setData,
        exportCall: settingsExport,
        loading: loading || loadingExport,
    });

	const [assignGroupingsModal, openAssignGroupingsModal] = useAssignGroupings({ resource: "location" });

	return (
		<div className="d-flex h-100">
			<Card className="flex-fill mb-0">
				<Topbar
					title={t("locations")}
					add={canAdd && goToCreate}
					help={goToHelp}
					importFile={openImportModal}
					onExport={onExport}
					loading={loading || loadingExport}
				/>

				<Card
					className="mb-auto"
					style={{ maxHeight: "calc(100% - 156px)" }}
				>
                    <Actions
						filters={table.getState().columnFilters}
						setFilters={table.setColumnFilters}
						columns={table.getAllColumns()}
                    >
                        <BulkActions
							rows={table.getState().rowSelection}
                            batchRemove={openDeleteModal}
							assignGroupings={openAssignGroupingsModal}
                        />
                    </Actions>

                    <NTable table={table} />
				</Card>
			</Card>

			<Drawer open={drawerOpen} close={goToView} title={drawerTitle}>
				{isFormOpen ? (
					<Form
						{...formProps}
						error={error}
						loading={submitting}
						close={goToView}
					/>
				) : (
					<>{t("help")}</>
				)}
			</Drawer>

            {importModal}

			{deleteModal}

			{unarchiveModal}

			{assignGroupingsModal}
		</div>
	);
}

export default Locations;
